import { useMemo, useState } from "react";
import type { CustomerItem } from "../../../mocks/customers";
import type {
  KullaniciDurum,
  KullaniciItem,
  KullaniciRol,
} from "../../../mocks/kullanicilar";
import { useCustomers } from "../../../hooks/useCustomers";
import { useAnimals } from "../../../hooks/useAnimals";
import { usePayments } from "../../../hooks/usePayments";
import { useUsers } from "../../../hooks/useUsers";
import { resetOperatorDefaultPasswordRequest } from "../../../lib/api";
import { deleteCustomerCascade } from "../../../lib/cascadeOps";
import { exportRowsToCsv, type ExportColumn } from "../../../lib/excelExport";
import {
  getNextUserId,
  isBootstrapAdmin,
  normalizePhone,
  normalizeText,
  rehydrateUsers,
  type UserRecord,
} from "../../../store/usersStore";
import UserRow from "./components/UserRow";
import OperatorAccountsSection from "./components/OperatorAccountsSection";
import UserModal, { type UserFormData } from "./components/UserModal";
import { sendSmsByTemplate } from "../../../lib/smsService";

const ALL_ROLES: (KullaniciRol | "Tümü")[] = [
  "Tümü",
  "Admin",
  "Muhasebe",
  "Kesim Alanı",
  "Taşıyıcı",
  "Müşteri",
  "Sıra Operatörü",
  "Kesim Operatörü",
  "Parçalama Operatörü",
  "Tamamlama Operatörü",
];

const PLACEHOLDER_OFFSET = 200000;

const OPERATOR_META = [
  {
    role: "Sıra Operatörü" as const,
    username: "sira.operator",
    title: "Sıra Operatörü",
    path: "/panel/istasyon/sira",
    defaultPassword: "Evren!Sira2026",
    color: "text-indigo-700",
    bg: "bg-indigo-100",
    icon: "ri-play-circle-line",
  },
  {
    role: "Kesim Operatörü" as const,
    username: "kesim.operator",
    title: "Kesim Operatörü",
    path: "/panel/istasyon/kesim",
    defaultPassword: "Evren!Kesim2026",
    color: "text-rose-700",
    bg: "bg-rose-100",
    icon: "ri-scissors-cut-line",
  },
  {
    role: "Parçalama Operatörü" as const,
    username: "parcalama.operator",
    title: "Parçalama Operatörü",
    path: "/panel/istasyon/parcalama",
    defaultPassword: "Evren!Parca2026",
    color: "text-violet-700",
    bg: "bg-violet-100",
    icon: "ri-layout-grid-line",
  },
  {
    role: "Tamamlama Operatörü" as const,
    username: "tamamlama.operator",
    title: "Tamamlama Operatörü",
    path: "/panel/istasyon/tamamlama",
    defaultPassword: "Evren!Tamam2026",
    color: "text-teal-700",
    bg: "bg-teal-100",
    icon: "ri-checkbox-circle-line",
  },
];

const OPERATOR_ROLE_SET = new Set<KullaniciRol>(OPERATOR_META.map((item) => item.role));
const OPERATOR_USERNAME_SET = new Set(OPERATOR_META.map((item) => item.username));

type UiUser = KullaniciItem & {
  customerId?: number;
  kisaNot?: string;
  hasLoginAccount: boolean;
  accountId?: number;
  forcePasswordChange?: boolean;
  smsOperationalOptIn?: boolean | null;
  smsMarketingOptIn?: boolean | null;
};

interface ExportUserRow {
  adSoyad: string;
  telefon: string;
  email: string;
  username: string;
  rol: string;
  durum: string;
  hesapDurumu: string;
  bagliMusteriId: string;
  sonGiris: string;
  ilkKayit: string;
  sifreDegistirsin: string;
  smsOperasyonel: string;
  smsPazarlama: string;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function fmtDate(value?: string) {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return value;
  }
}

function findCustomerAccount(customer: CustomerItem, users: UserRecord[]) {
  return (
    users.find(
      (user) =>
        user.rol === "Müşteri" &&
        (user.customerId === customer.id ||
          normalizePhone(user.telefon) === normalizePhone(customer.telefon) ||
          normalizeText(user.adSoyad) === normalizeText(customer.adSoyad))
    ) ?? null
  );
}

function isLockedBootstrapUser(user: UiUser) {
  return Boolean(
    user.hasLoginAccount &&
      user.accountId &&
      isBootstrapAdmin({
        id: user.accountId,
        telefon: user.telefon,
      })
  );
}

function isProtectedOperatorUser(user: UiUser) {
  return Boolean(
    user.hasLoginAccount &&
      user.accountId &&
      (OPERATOR_ROLE_SET.has(user.rol) || OPERATOR_USERNAME_SET.has(user.username ?? ""))
  );
}

export default function KullaniciYonetimiPage() {
  const { customers, setAllCustomers } = useCustomers();
  const { animals, setAllAnimals } = useAnimals();
  const { payments, setAllPayments } = usePayments();
  const { users, setAllUsers } = useUsers();

  const [search, setSearch] = useState("");
  const [rolFilter, setRolFilter] = useState<KullaniciRol | "Tümü">("Tümü");
  const [durumFilter, setDurumFilter] = useState<KullaniciDurum | "Tümü">(
    "Tümü"
  );
  const [onlyNoLogin, setOnlyNoLogin] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | null>(null);
  const [editUser, setEditUser] = useState<UiUser | null>(null);
  const [pwResetUser, setPwResetUser] = useState<UiUser | null>(null);
  const [newPw, setNewPw] = useState("");
  const [pwCopied, setPwCopied] = useState(false);

  const staffRows = useMemo<UiUser[]>(() => {
    return users
      .filter((user) => user.rol !== "Müşteri")
      .map((user) => ({
        id: user.id,
        adSoyad: user.adSoyad,
        telefon: user.telefon,
        email: user.email,
        username: user.username,
        rol: user.rol,
        durum: user.durum,
        eklemeTarihi: user.eklemeTarihi,
        sonGiris: user.sonGiris,
        hasLoginAccount: true,
        accountId: user.id,
        kisaNot: undefined,
        forcePasswordChange: user.forcePasswordChange,
        smsOperationalOptIn: user.smsOperationalOptIn ?? null,
        smsMarketingOptIn: user.smsMarketingOptIn ?? null,
      }));
  }, [users]);

  const customerRows = useMemo<UiUser[]>(() => {
    return (customers ?? []).map((customer) => {
      const account = findCustomerAccount(customer, users);

      return {
        id: account?.id ?? PLACEHOLDER_OFFSET + customer.id,
        adSoyad: customer.adSoyad,
        telefon: customer.telefon,
        email: account?.email,
        username: account?.username,
        rol: "Müşteri",
        durum: account?.durum ?? "Pasif",
        eklemeTarihi: account?.eklemeTarihi ?? todayIso(),
        sonGiris: account?.sonGiris,
        customerId: customer.id,
        kisaNot: customer.kisaNot,
        hasLoginAccount: !!account,
        accountId: account?.id,
        forcePasswordChange: account?.forcePasswordChange,
        smsOperationalOptIn: account?.smsOperationalOptIn ?? null,
        smsMarketingOptIn: account?.smsMarketingOptIn ?? null,
      };
    });
  }, [customers, users]);

  const allRows = useMemo<UiUser[]>(() => {
    return [...staffRows, ...customerRows].sort((a, b) => {
      const aDate = a.eklemeTarihi ?? "";
      const bDate = b.eklemeTarihi ?? "";
      return (
        bDate.localeCompare(aDate) ||
        a.adSoyad.localeCompare(b.adSoyad, "tr", { sensitivity: "base" })
      );
    });
  }, [staffRows, customerRows]);

  const filtered = useMemo(() => {
    return allRows.filter((user) => {
      if (search.trim()) {
        const q = search.toLocaleLowerCase("tr-TR");

        if (
          !user.adSoyad.toLocaleLowerCase("tr-TR").includes(q) &&
          !user.telefon.includes(q) &&
          !(user.email ?? "").toLocaleLowerCase("tr-TR").includes(q)
        ) {
          return false;
        }
      }

      if (rolFilter !== "Tümü" && user.rol !== rolFilter) return false;
      if (durumFilter !== "Tümü" && user.durum !== durumFilter) return false;
      if (onlyNoLogin && user.hasLoginAccount) return false;

      return true;
    });
  }, [allRows, search, rolFilter, durumFilter, onlyNoLogin]);

  const stats = useMemo(() => {
    return {
      toplam: allRows.length,
      aktif: allRows.filter((user) => user.durum === "Aktif").length,
      personel: staffRows.length,
      musteri: customerRows.length,
      operatorler: allRows.filter((user) => OPERATOR_ROLE_SET.has(user.rol)).length,
      girisiOlmayan: allRows.filter((user) => !user.hasLoginAccount).length,
    };
  }, [allRows, staffRows, customerRows]);

  const operatorCards = useMemo(() => {
    return OPERATOR_META.map((meta) => ({
      meta,
      user:
        allRows.find(
          (item) => item.username === meta.username || item.rol === meta.role
        ) ?? null,
    }));
  }, [allRows]);

  async function handleOperatorResetToDefault(user: UiUser) {
    if (!user.accountId) return;

    try {
      const result = await resetOperatorDefaultPasswordRequest(user.accountId);
      await rehydrateUsers();
      setPwResetUser({ ...user, username: result.username });
      setNewPw(result.default_password);
      setPwCopied(false);
      window.alert("Operatör şifresi varsayılana döndürüldü.");
    } catch (error) {
      window.alert(error instanceof Error ? error.message : "Operatör şifresi sıfırlanamadı.");
    }
  }

  function copyUsername(username: string) {
    navigator.clipboard.writeText(username).catch(() => {});
    window.alert(`Kullanıcı adı kopyalandı: ${username}`);
  }

  function exportCurrent() {
    const rows: ExportUserRow[] = filtered.map((user) => ({
      adSoyad: user.adSoyad,
      telefon: user.telefon,
      email: user.email ?? "",
      username: user.username ?? "",
      rol: user.rol,
      durum: user.durum,
      hesapDurumu: user.hasLoginAccount ? "Giriş hesabı var" : "Giriş hesabı yok",
      bagliMusteriId: user.customerId ? String(user.customerId) : "",
      sonGiris: fmtDate(user.sonGiris),
      ilkKayit: fmtDate(user.eklemeTarihi),
      sifreDegistirsin: user.forcePasswordChange ? "Evet" : "Hayır",
      smsOperasyonel:
        user.smsOperationalOptIn === null
          ? "Seçilmedi"
          : user.smsOperationalOptIn
          ? "İzinli"
          : "İzinsiz",
      smsPazarlama:
        user.smsMarketingOptIn === null
          ? "Seçilmedi"
          : user.smsMarketingOptIn
          ? "İzinli"
          : "İzinsiz",
    }));

    const columns: ExportColumn<ExportUserRow>[] = [
      { key: "adSoyad", label: "Ad Soyad" },
      { key: "telefon", label: "Telefon" },
      { key: "email", label: "E-posta" },
      { key: "username", label: "Kullanıcı Adı" },
      { key: "rol", label: "Rol" },
      { key: "durum", label: "Durum" },
      { key: "hesapDurumu", label: "Hesap Durumu" },
      { key: "bagliMusteriId", label: "Bağlı Müşteri ID" },
      { key: "sonGiris", label: "Son Giriş" },
      { key: "ilkKayit", label: "İlk Kayıt" },
      { key: "sifreDegistirsin", label: "Şifre Değişsin" },
      { key: "smsOperasyonel", label: "SMS Operasyonel" },
      { key: "smsPazarlama", label: "SMS Pazarlama" },
    ];

    exportRowsToCsv(
      `kullanici-yonetimi-${new Date().toISOString().slice(0, 10)}`,
      rows,
      columns
    );
  }

  function handleSave(data: UserFormData) {
    if (modalMode === "edit" && editUser && isLockedBootstrapUser(editUser)) {
      window.alert("Bootstrap admin düzenlenemez.");
      return;
    }

    if (modalMode === "edit" && editUser && isProtectedOperatorUser(editUser)) {
      data.rol = editUser.rol;
    }

    if (data.rol === "Müşteri") {
      if (modalMode === "add") {
        const nextCustomerId =
          Math.max(0, ...(customers ?? []).map((customer) => customer.id)) + 1;
        const initialPassword = data.sifre?.trim() || "degistir123";

        const newCustomer: CustomerItem = {
          id: nextCustomerId,
          adSoyad: data.adSoyad,
          telefon: data.telefon,
          kisaNot: data.kisaNot || undefined,
          toplamHisseSayisi: 0,
          toplamBorc: 0,
          toplamOdenen: 0,
        };

        void setAllCustomers((prev) => [newCustomer, ...prev]);

        void setAllUsers((prev) => [
          {
            id: getNextUserId(),
            adSoyad: data.adSoyad,
            telefon: data.telefon,
            email: data.email,
            rol: "Müşteri",
            durum: data.durum,
            eklemeTarihi: todayIso(),
            sonGiris: undefined,
            sifre: initialPassword,
            customerId: newCustomer.id,
            forcePasswordChange: true,
            smsOperationalOptIn: null,
            smsMarketingOptIn: null,
          },
          ...prev,
        ]);

        void sendSmsByTemplate({
          templateKey: "welcomeTempPassword",
          channel: "auth",
          eventType: "welcome_temp_password",
          phone: data.telefon,
          customerId: newCustomer.id,
          variables: {
            customerName: data.adSoyad,
            password: initialPassword,
          },
        });
      } else if (modalMode === "edit" && editUser?.customerId) {
        const targetCustomerId = editUser.customerId;

        void setAllCustomers((prev) =>
          prev.map((customer) =>
            customer.id === targetCustomerId
              ? {
                  ...customer,
                  adSoyad: data.adSoyad,
                  telefon: data.telefon,
                  kisaNot: data.kisaNot || undefined,
                }
              : customer
          )
        );

        if (editUser.hasLoginAccount && editUser.accountId) {
          void setAllUsers((prev) =>
            prev.map((user) =>
              user.id === editUser.accountId
                ? {
                    ...user,
                    adSoyad: data.adSoyad,
                    telefon: data.telefon,
                    email: data.email,
                    durum: data.durum,
                    forcePasswordChange: data.forcePasswordChange,
                    smsOperationalOptIn: data.smsOperationalOptIn,
                    smsMarketingOptIn: data.smsMarketingOptIn,
                  }
                : user
            )
          );
        }
      }

      return;
    }

    if (modalMode === "add") {
      const initialPassword = data.sifre?.trim() || "degistir123";

      void setAllUsers((prev) => [
        {
          id: getNextUserId(),
          adSoyad: data.adSoyad,
          telefon: data.telefon,
          email: data.email,
          rol: data.rol,
          durum: data.durum,
          eklemeTarihi: todayIso(),
          sonGiris: undefined,
          sifre: initialPassword,
          forcePasswordChange: data.forcePasswordChange,
          smsOperationalOptIn: data.smsOperationalOptIn,
          smsMarketingOptIn: data.smsMarketingOptIn,
        },
        ...prev,
      ]);

      void sendSmsByTemplate({
        templateKey: "welcomeTempPassword",
        channel: "auth",
        eventType: "welcome_temp_password",
        phone: data.telefon,
        variables: {
          customerName: data.adSoyad,
          password: initialPassword,
        },
      });
    } else if (modalMode === "edit" && editUser?.accountId) {
      void setAllUsers((prev) =>
        prev.map((user) =>
          user.id === editUser.accountId
            ? {
                ...user,
                adSoyad: data.adSoyad,
                telefon: data.telefon,
                email: data.email,
                rol: data.rol,
                durum: data.durum,
                forcePasswordChange: data.forcePasswordChange,
                smsOperationalOptIn: data.smsOperationalOptIn,
                smsMarketingOptIn: data.smsMarketingOptIn,
              }
            : user
        )
      );
    }
  }

  function handleToggleDurum(user: UiUser) {
    if (isLockedBootstrapUser(user)) {
      window.alert("Bootstrap admin pasife alınamaz.");
      return;
    }

    if (!user.hasLoginAccount || !user.accountId) return;

    void setAllUsers((prev) =>
      prev.map((item) =>
        item.id === user.accountId
          ? { ...item, durum: item.durum === "Aktif" ? "Pasif" : "Aktif" }
          : item
      )
    );
  }

  function handlePasswordReset(user: UiUser) {
    if (isLockedBootstrapUser(user)) {
      window.alert("Bootstrap admin şifresi bu ekrandan değiştirilemez.");
      return;
    }

    if (isProtectedOperatorUser(user)) {
      void handleOperatorResetToDefault(user);
      return;
    }

    const generated = Math.random().toString(36).slice(-10) + "!";

    if (user.rol === "Müşteri" && user.customerId) {
      if (user.hasLoginAccount && user.accountId) {
        void setAllUsers((prev) =>
          prev.map((item) =>
            item.id === user.accountId
              ? {
                  ...item,
                  sifre: generated,
                  forcePasswordChange: true,
                  smsOperationalOptIn: null,
                  smsMarketingOptIn: null,
                }
              : item
          )
        );
      } else {
        void setAllUsers((prev) => [
          {
            id: getNextUserId(),
            adSoyad: user.adSoyad,
            telefon: user.telefon,
            email: user.email,
            rol: "Müşteri",
            durum: "Aktif",
            eklemeTarihi: todayIso(),
            sonGiris: undefined,
            sifre: generated,
            customerId: user.customerId,
            forcePasswordChange: true,
            smsOperationalOptIn: null,
            smsMarketingOptIn: null,
          },
          ...prev,
        ]);
      }
    } else if (user.accountId) {
      void setAllUsers((prev) =>
        prev.map((item) =>
          item.id === user.accountId
            ? {
                ...item,
                sifre: generated,
                forcePasswordChange: true,
              }
            : item
        )
      );
    }

    void sendSmsByTemplate({
      templateKey: "passwordReset",
      channel: "auth",
      eventType: "password_reset",
      phone: user.telefon,
      customerId: user.customerId,
      variables: {
        customerName: user.adSoyad,
        password: generated,
      },
    });

    setPwResetUser(user);
    setNewPw(generated);
    setPwCopied(false);
  }

  function handleDelete(user: UiUser) {
    if (isLockedBootstrapUser(user)) {
      window.alert("Bootstrap admin silinemez.");
      return;
    }

    if (isProtectedOperatorUser(user)) {
      window.alert("Sabit operatör hesapları silinemez.");
      return;
    }

    if (user.rol === "Admin") {
      const activeAdminCount = users.filter((item) => item.rol === "Admin").length;
      if (activeAdminCount <= 1) {
        window.alert("Son admin hesabı silinemez.");
        return;
      }
    }

    if (user.rol === "Müşteri") {
      if (!user.customerId) {
        window.alert("Bu müşteri kaydı bulunamadı.");
        return;
      }

      const ok = window.confirm(
        `${user.adSoyad} müşterisi tamamen silinsin mi?\n\n` +
          `Bu işlem:\n` +
          `- müşteri kaydını\n` +
          `- müşteriye ait hisseleri\n` +
          `- müşteriye ait ödemeleri\n` +
          `- varsa giriş hesabını\n` +
          `kalıcı olarak siler.`
      );
      if (!ok) return;

      const result = deleteCustomerCascade({
        customerId: user.customerId,
        animals,
        customers,
        payments,
      });

      void setAllAnimals(() => result.animals);
      void setAllCustomers(result.customers);
      void setAllPayments(() => result.payments);

      if (user.accountId) {
        void setAllUsers((prev) =>
          prev.filter((item) => item.id !== user.accountId)
        );
      }

      return;
    }

    const ok = window.confirm(`${user.adSoyad} hesabı silinsin mi?`);
    if (!ok) return;

    if (user.accountId) {
      void setAllUsers((prev) => prev.filter((item) => item.id !== user.accountId));
    }
  }

  function copyPassword() {
    if (!newPw) return;
    navigator.clipboard.writeText(newPw).catch(() => {});
    setPwCopied(true);
    setTimeout(() => setPwCopied(false), 2000);
  }

  return (
    <div className="min-h-screen bg-farm-cream font-body">
      <div className="max-w-6xl mx-auto px-3 sm:px-5 py-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center bg-farm-earth-pale text-farm-earth rounded-lg">
                <i className="ri-team-line text-sm" />
              </div>
              <h1 className="font-heading font-bold text-base text-farm-brown">
                Kullanıcı Yönetimi
              </h1>
            </div>
            <p className="text-xs text-farm-muted mt-0.5 ml-9">
              Personel + müşteri kayıtları
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={exportCurrent}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-farm-border bg-white text-farm-brown text-xs font-semibold hover:border-farm-earth/40"
            >
              <i className="ri-file-excel-2-line text-base" />
              Excel Çıktısı
            </button>

            <button
              type="button"
              onClick={() => {
                setEditUser(null);
                setModalMode("add");
              }}
              className="flex items-center gap-1.5 px-3 py-2 bg-farm-earth hover:bg-farm-earth/90 text-white text-xs font-semibold rounded-farm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-user-add-line text-sm" />
              Yeni Kullanıcı Ekle
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2">
          {[
            {
              label: "Toplam Kullanıcı",
              value: stats.toplam,
              icon: "ri-group-line",
              bg: "bg-farm-earth-pale",
              color: "text-farm-earth",
            },
            {
              label: "Aktif",
              value: stats.aktif,
              icon: "ri-user-follow-line",
              bg: "bg-farm-green-pale",
              color: "text-farm-green",
            },
            {
              label: "Personel",
              value: stats.personel,
              icon: "ri-briefcase-line",
              bg: "bg-amber-50",
              color: "text-amber-700",
            },
            {
              label: "Müşteri Kaydı",
              value: stats.musteri,
              icon: "ri-user-line",
              bg: "bg-sky-50",
              color: "text-sky-700",
            },
            {
              label: "Operatör",
              value: stats.operatorler,
              icon: "ri-settings-3-line",
              bg: "bg-violet-50",
              color: "text-violet-700",
            },
            {
              label: "Girişi Yok",
              value: stats.girisiOlmayan,
              icon: "ri-door-lock-line",
              bg: "bg-gray-100",
              color: "text-gray-700",
            },
          ].map((item) => (
            <div
              key={item.label}
              className="bg-farm-warm border border-farm-border rounded-farm px-3 py-2.5 flex items-center gap-2.5"
            >
              <div
                className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${item.bg} ${item.color}`}
              >
                <i className={`${item.icon} text-sm`} />
              </div>
              <div>
                <p className={`font-bold text-xl leading-none ${item.color}`}>
                  {item.value}
                </p>
                <p className="text-[10px] text-farm-muted mt-0.5">
                  {item.label}
                </p>
              </div>
            </div>
          ))}
        </div>


        <OperatorAccountsSection
          items={operatorCards}
          onCopyUsername={copyUsername}
          onResetDefaultPassword={(user) => {
            void handleOperatorResetToDefault(user as UiUser);
          }}
          onEdit={(user) => {
            setEditUser(user as UiUser);
            setModalMode("edit");
          }}
          onToggleDurum={(user) => handleToggleDurum(user as UiUser)}
        />

        <div className="bg-farm-warm border border-farm-border rounded-farm px-3 py-3 space-y-3">
          <div className="flex flex-col md:flex-row gap-2">
            <div className="relative flex-1">
              <div className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-farm-muted pointer-events-none">
                <i className="ri-search-line text-xs" />
              </div>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Ad, telefon veya e-posta..."
                className="w-full pl-7 pr-3 h-9 text-xs border border-farm-border rounded-farm bg-farm-cream text-farm-brown placeholder-farm-muted focus:outline-none focus:border-farm-earth"
              />
            </div>

            <div className="flex gap-1 flex-wrap">
              {(["Tümü", "Aktif", "Pasif"] as const).map((durum) => (
                <button
                  key={durum}
                  type="button"
                  onClick={() => setDurumFilter(durum)}
                  className={`px-3 h-9 text-xs font-semibold rounded-farm border cursor-pointer transition-colors whitespace-nowrap ${
                    durumFilter === durum
                      ? "bg-farm-earth text-white border-farm-earth"
                      : "bg-farm-cream text-farm-muted border-farm-border hover:border-farm-earth/40"
                  }`}
                >
                  {durum}
                </button>
              ))}

              <button
                type="button"
                onClick={() => setOnlyNoLogin((prev) => !prev)}
                className={`px-3 h-9 text-xs font-semibold rounded-farm border cursor-pointer transition-colors whitespace-nowrap ${
                  onlyNoLogin
                    ? "bg-farm-brown text-white border-farm-brown"
                    : "bg-farm-cream text-farm-muted border-farm-border hover:border-farm-brown/40"
                }`}
              >
                Girişi olmayanlar
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {ALL_ROLES.map((rol) => {
              const active = rolFilter === rol;

              if (rol === "Tümü") {
                return (
                  <button
                    key="Tümü"
                    type="button"
                    onClick={() => setRolFilter("Tümü")}
                    className={`h-7 px-3 text-xs font-semibold rounded-full border cursor-pointer transition-colors whitespace-nowrap ${
                      active
                        ? "bg-farm-brown text-white border-farm-brown"
                        : "bg-farm-cream text-farm-muted border-farm-border hover:border-farm-earth/40"
                    }`}
                  >
                    Tüm Roller
                  </button>
                );
              }

              return (
                <button
                  key={rol}
                  type="button"
                  onClick={() => setRolFilter(rol)}
                  className={`h-7 px-3 text-xs font-semibold rounded-full border cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1 ${
                    active
                      ? "bg-farm-earth-pale text-farm-earth border-farm-earth/30"
                      : "bg-farm-cream text-farm-muted border-farm-border hover:border-farm-earth/40"
                  }`}
                >
                  {rol}
                  <span
                    className={`text-[10px] font-bold ml-0.5 px-1 rounded-full ${
                      active ? "bg-white/40" : "bg-farm-border text-farm-muted"
                    }`}
                  >
                    {allRows.filter((user) => user.rol === rol).length}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-farm-warm border border-farm-border rounded-farm overflow-hidden">
          <div
            className="hidden md:grid bg-farm-cream border-b border-farm-border text-[10px] font-semibold text-farm-muted uppercase tracking-wide"
            style={{
              gridTemplateColumns:
                "40px 190px 130px 110px 90px 1fr 70px 70px 70px",
            }}
          >
            {[
              "",
              "Ad Soyad",
              "Telefon",
              "Rol",
              "Durum",
              "Son Giriş",
              "Şifre",
              "Düzenle",
              "Sil",
            ].map((header, index) => (
              <div key={index} className="px-2 py-2">
                {header}
              </div>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="py-12 text-center">
              <div className="w-10 h-10 flex items-center justify-center mx-auto text-farm-muted mb-2">
                <i className="ri-user-search-line text-3xl" />
              </div>
              <p className="text-sm text-farm-muted">Kullanıcı bulunamadı.</p>
            </div>
          ) : (
            filtered.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                locked={isLockedBootstrapUser(user)}
                onEdit={() => {
                  setEditUser(user);
                  setModalMode("edit");
                }}
                onToggleDurum={() => handleToggleDurum(user)}
                onPasswordReset={() => handlePasswordReset(user)}
                onDelete={() => handleDelete(user)}
              />
            ))
          )}
        </div>

        <p className="text-[10px] text-farm-muted text-center">
          Sistem bootstrap admini ve sabit operatör hesaplarını korur. Operatör kullanıcı adları sabittir; şifreler varsayılana döndürülebilir.
        </p>
      </div>

      {modalMode && (
        <UserModal
          mode={modalMode}
          user={editUser}
          forceRole={modalMode === "edit" && editUser && isProtectedOperatorUser(editUser) ? editUser.rol : undefined}
          titleOverride={modalMode === "edit" && editUser && isProtectedOperatorUser(editUser) ? "Operatör Hesabını Düzenle" : undefined}
          subtitleOverride={modalMode === "edit" && editUser && isProtectedOperatorUser(editUser) ? "Bu hesapta kullanıcı adı ve rol sabittir. Durum ve temel bilgiler yönetilebilir." : undefined}
          onSave={handleSave}
          onClose={() => {
            setModalMode(null);
            setEditUser(null);
          }}
        />
      )}

      {pwResetUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-3">
          <div className="bg-farm-cream border border-farm-border rounded-farm w-full max-w-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-farm-border bg-farm-warm">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center bg-farm-earth-pale text-farm-earth rounded-lg">
                  <i className="ri-lock-password-line text-sm" />
                </div>
                <p className="font-heading font-semibold text-sm text-farm-brown">
                  Şifre Sıfırla
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPwResetUser(null)}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-farm-muted hover:bg-farm-border cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="px-4 py-4 space-y-3">
              <p className="text-xs text-farm-muted">
                <span className="font-semibold text-farm-brown">
                  {pwResetUser.adSoyad}
                </span>{" "}
                için yeni şifre kaydedildi.
              </p>

              <div className="flex items-center gap-2 bg-farm-warm border border-farm-border rounded-farm px-3 py-2.5">
                <p className="flex-1 font-mono text-sm font-bold text-farm-brown tracking-wider">
                  {newPw}
                </p>
                <button
                  type="button"
                  onClick={copyPassword}
                  className={`px-2.5 h-7 text-xs font-semibold rounded border cursor-pointer transition-colors whitespace-nowrap ${
                    pwCopied
                      ? "bg-farm-green-pale text-farm-green border-farm-green/20"
                      : "bg-farm-earth-pale text-farm-earth border-farm-earth/20 hover:bg-farm-earth hover:text-white"
                  }`}
                >
                  {pwCopied ? (
                    <>
                      <i className="ri-check-line mr-0.5" />
                      Kopyalandı
                    </>
                  ) : (
                    <>
                      <i className="ri-file-copy-line mr-0.5" />
                      Kopyala
                    </>
                  )}
                </button>
              </div>

              <div className="flex items-start gap-1.5 text-[10px] text-farm-earth bg-farm-gold-pale/60 border border-farm-gold/20 rounded px-2.5 py-2">
                <i className="ri-alert-line text-xs flex-shrink-0 mt-0.5" />
                <span>
                  Bu şifre artık kaydedildi. Kullanıcı bu şifre ile giriş yapabilir.
                </span>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPwResetUser(null)}
                  className="flex-1 h-9 text-xs font-semibold border border-farm-border text-farm-muted rounded-farm bg-farm-warm hover:bg-farm-border cursor-pointer transition-colors whitespace-nowrap"
                >
                  Kapat
                </button>
                <button
                  type="button"
                  onClick={() => {
                    copyPassword();
                    setTimeout(() => setPwResetUser(null), 800);
                  }}
                  className="flex-1 h-9 text-xs font-semibold bg-farm-earth hover:bg-farm-earth/90 text-white rounded-farm cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-file-copy-line mr-1" />
                  Kopyala ve Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}